import React, { createContext, useContext, useState, useCallback } from 'react';
import { globalConstants } from '../constants/globalConstant';

/**
 * Widget Builder Context - Manages UI state for widget configuration
 *
 * Replaces Redux widgetTemplateListSlice UI state:
 * - selectedWidget (mode, widgetTitle, widgetDetails)
 * - selectedWidgetConfiguration (metaData, metaValue)
 * - selectedWidgetListTab
 */

export const WidgetBuilderContext = createContext(null);

export function WidgetBuilderProvider({ children }) {
  // Selected widget state (CREATE/UPDATE mode)
  const [selectedWidget, setSelectedWidget] = useState({
    mode: null,
    widgetTitle: null,
    widgetDetails: {}
  });

  // Widget configuration being edited
  const [selectedWidgetConfiguration, setSelectedWidgetConfiguration] = useState({
    metaData: {},
    metaValue: {}
  });

  // Active vs Experimental tab
  const [selectedWidgetListTab, setSelectedWidgetListTab] = useState(
    globalConstants.ACTIVE_WIDGETS
  );

  // Shared settings-panel footer contract (ConfigurationScreen renders the
  // footer; the active settings form can disable submit / change its label).
  const [footerConfig, setFooterConfig] = useState({
    submitDisabled: false,
    submitLabel: null,
    submitLoading: false,
    hideFooter: false
  });

  // Actions
  const changeSelectedWidget = useCallback(({ mode, widgetTitle, widgetDetails }) => {
    setSelectedWidget({ mode, widgetTitle, widgetDetails });
    setFooterConfig({
      submitDisabled: false,
      submitLabel: null,
      submitLoading: false,
      hideFooter: false
    });
  }, []);

  const createWidgetDefinition = useCallback((metaData) => {
    setSelectedWidgetConfiguration({
      metaData,
      metaValue: {}
    });
  }, []);

  const updateWidgetDefinition = useCallback(({ metaData, metaValue }) => {
    setSelectedWidgetConfiguration({ metaData, metaValue });
  }, []);

  const resetWidgetConfiguration = useCallback(() => {
    setSelectedWidget({
      mode: null,
      widgetTitle: null,
      widgetDetails: {}
    });
    setSelectedWidgetConfiguration({
      metaData: {},
      metaValue: {}
    });
    setFooterConfig({
      submitDisabled: false,
      submitLabel: null,
      submitLoading: false,
      hideFooter: false
    });
  }, []);

  const changeSelectedWidgetListTab = useCallback((key) => {
    setSelectedWidgetListTab(key);
  }, []);

  const value = {
    // State
    selectedWidget,
    selectedWidgetConfiguration,
    selectedWidgetListTab,
    footerConfig,

    // Actions
    changeSelectedWidget,
    createWidgetDefinition,
    updateWidgetDefinition,
    resetWidgetConfiguration,
    changeSelectedWidgetListTab,
    setFooterConfig
  };

  return <WidgetBuilderContext.Provider value={value}>{children}</WidgetBuilderContext.Provider>;
}

/**
 * Hook to access widget builder context
 *
 * Usage:
 * const { selectedWidget, changeSelectedWidget } = useWidgetBuilder();
 */
export function useWidgetBuilder() {
  const context = useContext(WidgetBuilderContext);
  if (!context) {
    throw new Error('useWidgetBuilder must be used within WidgetBuilderProvider');
  }
  return context;
}
